import React from 'react';

import { splitPromptForFillInBlank } from '../../../Shared/libs/splitPromptForFillInBlank';
import { filterBySuboptimalBlank } from '../../libs/filterResponsesByBlank';
import { BlankByBlankGrading, } from '../../../Shared/quill-marking-logic/src/interfaces'

interface Response {
  text: string;
  [key: string]: any;
}

interface Question {
  blankByBlankGrading?: BlankByBlankGrading;
  caseInsensitive?: boolean;
  prompt: string;
  [key: string]: any;
}

export interface FilterState {
  suboptimalBlankIndices: number[];
  searchValues: string[];
}

interface BlankFilterSectionProps {
  question: Question;
  responses: Response[];
  onFilterChange: (filterState: FilterState) => void;
  currentFilters: FilterState;
}

export const BlankFilterSection: React.FC<BlankFilterSectionProps> = ({
  question,
  responses,
  onFilterChange,
  currentFilters
}) => {
  const numberOfBlanks = splitPromptForFillInBlank(question.prompt).length - 1;

  const [searchInputs, setSearchInputs] = React.useState<string[]>(
    currentFilters.searchValues.length > 0
      ? currentFilters.searchValues
      : Array(numberOfBlanks).fill('')
  );

  React.useEffect(() => {
    if (currentFilters.searchValues.length > 0) {
      setSearchInputs(currentFilters.searchValues);
    }
  }, [currentFilters.searchValues]);

  const suboptimalCounts = Array.from({ length: numberOfBlanks }).map((_, index) => {
    return filterBySuboptimalBlank(responses, question, index).length;
  });

  const handleSuboptimalClick = (blankIndex: number) => {
    const currentIndices = currentFilters.suboptimalBlankIndices || [];
    const isSelected = currentIndices.includes(blankIndex);
    const newIndices = isSelected
      ? currentIndices.filter(idx => idx !== blankIndex)
      : [...currentIndices, blankIndex];

    onFilterChange({
      ...currentFilters,
      suboptimalBlankIndices: newIndices
    });
  };

  const handleSearchChange = (blankIndex: number, value: string) => {
    const newSearchInputs = [...searchInputs];
    newSearchInputs[blankIndex] = value;
    setSearchInputs(newSearchInputs);
  };

  const handleShowResults = () => {
    onFilterChange({
      ...currentFilters,
      searchValues: searchInputs
    });
  };

  const handleClearFilters = () => {
    const emptySearchValues = Array(numberOfBlanks).fill('');
    setSearchInputs(emptySearchValues);
    onFilterChange({
      suboptimalBlankIndices: [],
      searchValues: []
    });
  };

  if (numberOfBlanks === 0) {
    return null;
  }

  return (
    <div className="blank-filter-section">
      {question.blankByBlankGrading && (
        <div className="filter-buttons">
          {Array.from({ length: numberOfBlanks }).map((_, index) => {
            const isSelected = (currentFilters.suboptimalBlankIndices || []).includes(index);
            return (
              <button
                className={`quill-button medium green focus-on-light ${isSelected ? 'contained' : 'outlined'}`}
                key={index}
                onClick={() => handleSuboptimalClick(index)}
                type="button"
              >
                Blank {index + 1} suboptimal: {suboptimalCounts[index]}
              </button>
            );
          })}
        </div>
      )}

      <div className="search-fields">
        {Array.from({ length: numberOfBlanks }).map((_, index) => (
          <input
            className="input"
            key={index}
            onChange={(e) => handleSearchChange(index, e.target.value)}
            placeholder={`Enter a cue for blank ${index + 1}`}
            type="text"
            value={searchInputs[index] || ''}
          />
        ))}
      </div>

      <div className="action-buttons">
        <button className="quill-button contained medium green focus-on-light" onClick={handleShowResults} type="button">
          Show Results
        </button>
        <button className="quill-button outlined medium green focus-on-light" onClick={handleClearFilters} type="button">
          Clear Filters
        </button>
      </div>
    </div>
  );
};
